import { lazy, type ComponentType } from "react";
import type { Permission } from "@shared/permissions";
import type { PublicSite } from "@shared/types";

/**
 * ============================================================
 * THE ROUTE REGISTRY
 * ============================================================
 * Adding a page is a single entry here. The registry drives, at once:
 *   - the <Routes> tree            (App.tsx)
 *   - the header / admin nav       (SiteHeader.tsx, AdminLayout.tsx)
 *   - the access guard             (ProtectedRoute.tsx)
 *
 * `permissions` is a *client-side affordance only*. The matching API route
 * in worker/routes/ must apply the same requirePermission() guard — never
 * rely on this to keep data private.
 */

export type RouteArea = "site" | "account" | "admin";

export interface AppRoute {
  path: string;
  element: ComponentType;
  label?: string;
  area: RouteArea;
  auth?: boolean;
  permissions?: Permission[];
  masterOnly?: boolean;
  description?: string;
  order?: number;
  hidden?: boolean;
  /** Only in the nav while this public switch is on (see useSite). */
  flag?: keyof PublicSite;
}

/* ------------------------------------------------------------------ */
/* Public site                                                         */
/* ------------------------------------------------------------------ */

const Home = lazy(() => import("@/pages/Home"));
const About = lazy(() => import("@/pages/About"));
const Sketches = lazy(() => import("@/pages/Sketches"));
const SketchDetail = lazy(() => import("@/pages/SketchDetail"));
const Blog = lazy(() => import("@/pages/Blog"));
const Spotify = lazy(() => import("@/pages/Spotify"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const Alliance = lazy(() => import("@/pages/Alliance"));

/* ------------------------------------------------------------------ */
/* Account                                                             */
/* ------------------------------------------------------------------ */

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Settings = lazy(() => import("@/pages/Settings"));

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

const AdminOverview = lazy(() => import("@/pages/admin/Overview"));
const AdminSketches = lazy(() => import("@/pages/admin/Sketches"));
const AdminSketchEditor = lazy(() => import("@/pages/admin/SketchEditor"));
const AdminPosts = lazy(() => import("@/pages/admin/Posts"));
const AdminPostEditor = lazy(() => import("@/pages/admin/PostEditor"));
const AdminAppearance = lazy(() => import("@/pages/admin/Appearance"));
const AdminUtensils = lazy(() => import("@/pages/admin/Utensils"));
const AdminSpotify = lazy(() => import("@/pages/admin/Spotify"));
const AdminAlliance = lazy(() => import("@/pages/admin/Alliance"));
const AdminVisits = lazy(() => import("@/pages/admin/Visits"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminUserDetail = lazy(() => import("@/pages/admin/UserDetail"));
const AdminRoles = lazy(() => import("@/pages/admin/Roles"));
const AdminAudit = lazy(() => import("@/pages/admin/Audit"));

export const routes: AppRoute[] = [
  /* ---- public ---- */
  { path: "/", element: Home, label: "Home", area: "site", order: 0 },
  { path: "/about", element: About, label: "About", area: "site", order: 10 },
  { path: "/sketches", element: Sketches, label: "Sketches", area: "site", order: 15 },
  { path: "/sketches/:slug", element: SketchDetail, area: "site", hidden: true },
  { path: "/blog", element: Blog, label: "Blog", area: "site", order: 20 },
  // Linked from About and reachable directly. Drop `label` to keep it out
  // of the header while leaving the URL live.
  { path: "/listening", element: Spotify, label: "Listening", area: "site", order: 18 },
  { path: "/blog/:slug", element: BlogPost, area: "site", hidden: true },
  // Shown or hidden from Admin → Alliance.
  { path: "/alliance", element: Alliance, label: "Alliance", area: "site", order: 25, flag: "alliance" },

  /* ---- signed-in ---- */
  { path: "/dashboard", element: Dashboard, label: "Dashboard", area: "account", auth: true, order: 0 },
  { path: "/settings", element: Settings, label: "Settings", area: "account", auth: true, order: 10 },

  /* ---- admin ---- */
  {
    path: "/admin",
    element: AdminOverview,
    label: "Overview",
    area: "admin",
    permissions: ["admin.access"],
    order: 0,
  },
  {
    path: "/admin/sketches",
    element: AdminSketches,
    label: "Sketches",
    area: "admin",
    permissions: ["admin.access", "sketches.read"],
    description: "The p5 library",
    order: 10,
  },
  {
    path: "/admin/sketches/new",
    element: AdminSketchEditor,
    area: "admin",
    permissions: ["admin.access", "sketches.write"],
    hidden: true,
  },
  {
    path: "/admin/sketches/:id",
    element: AdminSketchEditor,
    area: "admin",
    permissions: ["admin.access", "sketches.read"],
    hidden: true,
  },
  {
    path: "/admin/posts",
    element: AdminPosts,
    label: "Blog",
    area: "admin",
    permissions: ["admin.access", "posts.read"],
    description: "Write and publish",
    order: 20,
  },
  {
    path: "/admin/posts/new",
    element: AdminPostEditor,
    area: "admin",
    permissions: ["admin.access", "posts.write"],
    hidden: true,
  },
  {
    path: "/admin/posts/:id",
    element: AdminPostEditor,
    area: "admin",
    permissions: ["admin.access", "posts.read"],
    hidden: true,
  },
  {
    path: "/admin/utensils",
    element: AdminUtensils,
    label: "Utensils",
    area: "admin",
    permissions: ["admin.access", "settings.write"],
    description: "The tools dropdown",
    order: 25,
  },
  {
    path: "/admin/spotify",
    element: AdminSpotify,
    label: "Spotify",
    area: "admin",
    permissions: ["admin.access", "spotify.write"],
    description: "Connect and publish",
    order: 27,
  },
  {
    path: "/admin/visits",
    element: AdminVisits,
    label: "Visits",
    area: "admin",
    permissions: ["admin.access", "visits.read"],
    description: "Who came, from where",
    order: 5,
  },
  {
    path: "/admin/alliance",
    element: AdminAlliance,
    label: "Alliance",
    area: "admin",
    permissions: ["admin.access", "alliance.read"],
    description: "Sign-ups and their emails",
    order: 28,
  },
  {
    path: "/admin/appearance",
    element: AdminAppearance,
    label: "Appearance",
    area: "admin",
    permissions: ["admin.access", "settings.write"],
    description: "Pick the site background",
    order: 30,
  },
  {
    path: "/admin/users",
    element: AdminUsers,
    label: "Users",
    area: "admin",
    permissions: ["admin.access", "users.read"],
    description: "Accounts and access",
    order: 40,
  },
  {
    path: "/admin/users/:id",
    element: AdminUserDetail,
    area: "admin",
    permissions: ["admin.access", "users.read"],
    hidden: true,
  },
  {
    path: "/admin/roles",
    element: AdminRoles,
    label: "Roles",
    area: "admin",
    permissions: ["admin.access", "roles.read"],
    description: "Permission bundles",
    order: 50,
  },
  {
    path: "/admin/audit",
    element: AdminAudit,
    label: "Audit log",
    area: "admin",
    permissions: ["admin.access", "audit.read"],
    description: "Who changed what",
    order: 60,
  },
];

export function navRoutes(area: RouteArea, site?: PublicSite): AppRoute[] {
  return routes
    .filter((r) => r.area === area && r.label && !r.hidden && (!r.flag || !site || site[r.flag]))
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

export const isProtected = (route: AppRoute): boolean =>
  Boolean(route.auth || route.masterOnly || route.permissions?.length);
